import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PropertyFilter } from 'support/types/filters';

import { DropdownPanelShell } from '../DropdownPanelShell';
import { FilterOptionRow } from '../FilterOptionRow';

type FilterDropdownPanelProps = {
  filter: PropertyFilter;
  onChange: (filterKey: string, newValue: string) => void;
};

export const FilterDropdownPanel: FC<FilterDropdownPanelProps> = ({ filter, onChange }) => {
  const { t } = useTranslation();
  const options = filter.dropDownOptions;
  const multiple = options?.multiple ?? false;
  const defaultToFirstValue = options?.defaultToFirstValue ?? false;

  const currentValues = useMemo(() => {
    if (filter.value) {
      return filter.value.split(',');
    }
    // With defaultToFirstValue the first option is functionally active even when the value is empty.
    const firstOption = options?.dropdownValues[0];
    if (defaultToFirstValue && firstOption) {
      return [String(firstOption.value)];
    }
    return [];
  }, [filter.value, defaultToFirstValue, options?.dropdownValues]);

  const isAllSelected = currentValues.length === 0;

  const handleSelectAll = () => onChange(filter.key, '');

  const handleToggle = (value: string) => {
    if (!multiple) {
      onChange(filter.key, value);
      return;
    }

    const nextValues = currentValues.includes(value)
      ? currentValues.filter((currentValue) => currentValue !== value)
      : [...currentValues, value];

    onChange(filter.key, nextValues.join(','));
  };

  // Multi-select reads as "pick any" (checkbox); single-select reads as "pick one" (radio).
  const controlType = multiple ? 'checkbox' : 'radio';

  return (
    <DropdownPanelShell
      filter={filter}
      header={
        // Multi-select reads "nothing ticked = all", so an explicit All row is redundant there.
        // Single-select needs it as the only way to clear back to no filter.
        !multiple && !defaultToFirstValue ? (
          <FilterOptionRow type={controlType} name={filter.key} selected={isAllSelected} onSelect={handleSelectAll}>
            {t('common:allValues')}
          </FilterOptionRow>
        ) : undefined
      }
    >
      {(filteredOptions) =>
        filteredOptions.map((option) => {
          const value = String(option.value);
          return (
            <FilterOptionRow
              key={value}
              type={controlType}
              name={filter.key}
              selected={currentValues.includes(value)}
              onSelect={() => handleToggle(value)}
              testId={`filter-option-${filter.key}-${value}`}
            >
              {t(option.label)}
            </FilterOptionRow>
          );
        })
      }
    </DropdownPanelShell>
  );
};
